import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import ProOpticDmpFlow from "./ProOpticDmpFlow";

const GAINS = [
  {
    t: "Vous voyez ce que vous ne voyiez pas",
    d: "La dernière ordonnance, le bilan orthoptique, le compte rendu de la dernière consultation. Plus besoin d'appeler le cabinet, ni de s'en remettre à la mémoire du patient.",
  },
  {
    t: "Votre travail ne se perd plus",
    d: "L'adaptation que vous venez de réaliser est déposée dans le dossier. Le professionnel suivant la retrouve, quel que soit son logiciel.",
  },
  {
    t: "Les 3 O sur la même page",
    d: "Ophtalmologiste, orthoptiste, opticien : chacun voit ce que les autres ont fait, dans l'ordre, avec les dates. C'est ça, la coordination.",
  },
];

const PATIENT = [
  "Il donne son accord avant le premier accès, et il peut le retirer quand il veut.",
  "Il retrouve chaque document déposé dans Mon espace santé, sur son téléphone.",
  "Il peut masquer un document : vous ne verrez pas ce qu'il ne souhaite pas montrer.",
  "Chaque consultation laisse une trace horodatée, qu'il peut lire lui-même.",
];

export default function ProOpticDmpPage() {
  return (
    <div className="pdg">
      <SEO
        title="Le dossier médical partagé, en consultation et en alimentation · MetaConnect"
        description="Depuis OhMyVue, l'opticien consulte le dossier médical partagé de son patient — bilan orthoptique, ordonnance, compte rendu — et y dépose son adaptation. Avec l'accord du patient, dans Mon espace santé."
        canonical="/idm-optic/dmp"
        noIndex
      />
      <style>{CSS}</style>

      <section className="pdg-hero">
        <div className="pdg-wrap">
          <span className="pdg-pill">Dossier médical partagé</span>
          <h1>Le dossier médical partagé, en consultation et en alimentation</h1>
          <p className="pdg-lede">
            Le DMP, c'est le carnet de santé numérique de votre patient. Depuis OhMyVue, vous
            l'ouvrez pour retrouver le bilan orthoptique ou l'ordonnance de l'ophtalmologiste — et
            vous y déposez votre travail, pour que les autres le retrouvent à leur tour.
          </p>

          <div className="pdg-logos">
            <img src="/logos/dmp.svg" alt="Dossier Médical Partagé" className="pdg-l1" />
            <span className="pdg-sep" aria-hidden="true" />
            <img src="/logos/mon-espace-sante.svg" alt="Mon espace santé" className="pdg-l2" />
          </div>
          <p className="pdg-lnote">
            Le dossier médical partagé vit dans <b>Mon espace santé</b>, l'espace personnel ouvert
            par l'Assurance Maladie à chaque assuré. Ce que vous y déposez, votre patient le retrouve
            chez lui.
          </p>
        </div>
      </section>

      <section className="pdg-sec">
        <div className="pdg-wrap">
          <div className="pdg-head">
            <h2>Vous consultez son dossier, vous y déposez le vôtre</h2>
            <p>Quatre gestes, depuis la fiche patient d'OhMyVue. Rien à installer sur votre poste.</p>
          </div>
          <ProOpticDmpFlow />
        </div>
      </section>

      <section className="pdg-sec pdg-alt">
        <div className="pdg-wrap">
          <div className="pdg-head">
            <h2>Ce que ça change dans votre magasin</h2>
          </div>
          <div className="pdg-grid">
            {GAINS.map((g) => (
              <div className="pdg-card" key={g.t}>
                <h3>{g.t}</h3>
                <p>{g.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pdg-sec">
        <div className="pdg-wrap">
          <div className="pdg-pat">
            <div className="pdg-patl">
              <img src="/logos/mon-espace-sante.svg" alt="Mon espace santé" />
              <h2>C'est le patient qui décide</h2>
              <p>
                Vous n'entrez jamais dans un dossier sans son accord. Il garde la main du début à la
                fin.
              </p>
            </div>
            <ul className="pdg-list">
              {PATIENT.map((x) => (
                <li key={x}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="pdg-more">
            <Link to="/idm-optic/message-type">
              Voir le message envoyé à l'ophtalmologiste ou à l'orthoptiste
              <em>&rsaquo;</em>
            </Link>
            <Link to="/idm-optic">
              Toutes les fonctionnalités ouvertes avec IDM Optic et IDM Audio
              <em>&rsaquo;</em>
            </Link>
          </div>
        </div>
      </section>

      <section className="pdg-cta">
        <div className="pdg-wrap">
          <h2>Vous voulez le voir tourner sur vos propres dossiers ?</h2>
          <p>Quinze minutes suffisent pour comprendre le parcours complet.</p>
          <Link className="pdg-btn" to="/demo">
            Prendre rendez-vous
          </Link>
        </div>
      </section>
    </div>
  );
}

const CSS = `
.pdg{
  --ground:#F5F5F5;--surface:#fff;--line:#E6E8EC;--text:#0F172A;--muted:#5A6478;
  --blue:#0080FF;--bsoft:#EAF4FF;--bline:#CFE4FF;
  background:var(--ground);color:var(--text);
  font-family:"Instrument Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  -webkit-font-smoothing:antialiased;
}
.pdg *{box-sizing:border-box}
.pdg h1,.pdg h2,.pdg h3{margin:0;color:var(--text);font-weight:600;text-wrap:balance}
.pdg h1{font-size:clamp(29px,3.6vw,40px);line-height:1.14;letter-spacing:-.02em}
.pdg p{margin:0;color:var(--muted);line-height:1.6}
.pdg ul{list-style:none;margin:0;padding:0}
.pdg-wrap{max-width:1000px;margin:0 auto;padding:0 28px}

.pdg-pill{display:inline-flex;border-radius:999px;background:#E7ECF3;color:#45526B;
  font-size:11.5px;font-weight:600;letter-spacing:.11em;text-transform:uppercase;padding:7px 15px}
.pdg-hero{padding:82px 0 40px;text-align:center}
.pdg-hero h1{margin:16px auto 0;max-width:22ch}
.pdg .pdg-lede{max-width:66ch;margin:16px auto 0;font-size:16.5px}

.pdg-logos{display:flex;align-items:center;justify-content:center;gap:24px;margin:32px auto 0;
  background:#fff;border:1px solid var(--line);border-radius:16px;padding:20px 28px;max-width:440px;
  box-shadow:0 18px 44px -34px rgba(15,23,42,.5)}
.pdg-l1{height:70px;width:auto;display:block}
.pdg-l2{height:56px;width:auto;display:block}
.pdg-sep{width:1px;height:48px;background:var(--line);display:block}
.pdg .pdg-lnote{max-width:60ch;margin:16px auto 0;font-size:13.6px}
.pdg .pdg-lnote b{color:var(--text);font-weight:600}

.pdg-sec{padding:56px 0}
.pdg-alt{background:#fff;border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.pdg-head{text-align:center;max-width:64ch;margin:0 auto}
.pdg-head h2{font-size:clamp(22px,2.6vw,30px);line-height:1.2;letter-spacing:-.018em}
.pdg-head p{margin-top:12px;font-size:15.5px}

.pdg-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-top:30px}
.pdg-card{background:var(--ground);border:1px solid var(--line);border-radius:14px;padding:22px 22px 24px}
.pdg-card h3{font-size:16px;line-height:1.3;margin-bottom:9px}
.pdg-card p{font-size:13.8px;line-height:1.62}

.pdg-pat{display:grid;grid-template-columns:300px 1fr;gap:28px;align-items:center;background:#fff;
  border:1px solid var(--line);border-radius:18px;padding:30px 32px}
.pdg-patl img{height:44px;width:auto;display:block;margin-bottom:16px}
.pdg-patl h2{font-size:22px;line-height:1.22;letter-spacing:-.015em}
.pdg-patl p{margin-top:10px;font-size:14px}
.pdg-list{display:grid;gap:12px}
.pdg-list li{display:flex;gap:12px;align-items:flex-start;font-size:14.2px;line-height:1.55;
  color:#3D4759}
.pdg-list svg{width:16px;height:16px;flex:0 0 16px;margin-top:3px;stroke:#1E8A4C;fill:none;
  stroke-width:2.4;stroke-linecap:round;stroke-linejoin:round}

.pdg-more{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-top:20px}
.pdg-more a{display:flex;align-items:center;justify-content:space-between;gap:12px;background:var(--bsoft);
  border:1px solid var(--bline);border-radius:13px;padding:16px 20px;text-decoration:none;
  color:#0B62C4;font-size:14.4px;font-weight:600;line-height:1.4;transition:background .18s}
.pdg-more a:hover{background:#DBEBFF}
.pdg-more em{font-style:normal;font-size:19px;line-height:1}

.pdg-cta{background:var(--blue);padding:56px 0;text-align:center}
.pdg-cta h2{color:#fff;font-size:clamp(22px,2.6vw,30px);margin-bottom:12px}
.pdg-cta p{color:rgba(255,255,255,.9);font-size:15.5px;max-width:60ch;margin:0 auto 24px}
.pdg-btn{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;
  background:#fff;color:var(--blue);font-weight:600;font-size:15px;text-decoration:none;
  padding:13px 28px}
.pdg-btn:hover{background:#EAF4FF;color:var(--blue)}

@media (max-width:880px){
  .pdg-grid{grid-template-columns:1fr}
  .pdg-pat{grid-template-columns:1fr;gap:20px;align-items:start}
  .pdg-more{grid-template-columns:1fr}
  .pdg-hero{padding:56px 0 32px}
}
@media (max-width:520px){
  .pdg-logos{flex-direction:column;gap:18px;padding:22px}
  .pdg-sep{width:44px;height:1px}
}
.pdg a:focus-visible{outline:2px solid var(--blue);outline-offset:3px;border-radius:6px}
`;
